import {
  Button,
  Container,
  HStack,
  Stack,
  Text,
  VStack,
  Spinner,
  IconButton,
  Image,
  Box,
  useToast,
  Progress,
  Divider,
  chakra,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback, useRef } from "react";
import actions from "../../actions";
import {
  KYDAdminFeed,
  KYDAdminFeedItem,
  KYDFeedItem,
  KYDSocialPlatforms,
} from "@common/interfaces/KYDOrganization";
import ReactS3Uploader from "react-s3-uploader";
import { FaPlus, FaCloudUploadAlt } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import { capitalized } from "../../utils";
import { Reorder } from "framer-motion";
import { FeedItem } from "../FeedItem";
import { PageViews } from "../PageViewsChart";
import "react-datepicker/dist/react-datepicker.css";
import { useDialog } from "../../Common/Dialog";
import EditLinkModal from "./EditLinkModal";
import SocialInput from "./SocialInput";
import { useDirtyText, useLoading, useProgress } from "../hooks/form";

const SOCIAL_NETWORKS = [
  "spotify",
  "twitter",
  "instagram",
  "soundcloud",
  "applemusic",
  "youtube",
];

const List = chakra(Reorder.Group);

const useModalType = <T extends string>(
  type: T = null
): [T, (type: T) => void, () => void] => {
  const [modalType, setModalType] = useState<T | null>(type);

  const close = useCallback(() => setModalType(null), [setModalType]);

  return [modalType, setModalType, close];
};

const FeedManager = () => {
  const [feed, setFeed] = useState<KYDAdminFeed | null>(null);
  const [loading, updateLoading] = useLoading({
    link: false,
    feed_image: false,
    blocking: false,
    init: false,
    news_order: false,
    music_order: false,
    social: false,
    tickettype: false,
  });

  const [progress, updateProgress] = useProgress({
    poster: 0,
  });

  const [dirtyText, updateDirtyText] = useDirtyText();
  const uploadRef = useRef<HTMLInputElement>(null);

  const dialog = useDialog();
  const toast = useToast();
  const [newsOrder, setNewsOrder] = useState<string[]>([]);
  const [musicOrder, setMusicOrder] = useState<string[]>([]);
  const [modalType, setModalType, closeModal] =
    useModalType<KYDFeedItem["type"]>();

  const musicOrderRef = useRef(null);
  const newsOrderRef = useRef(null);

  const [editingFeedItem, setEditingFeedItem] =
    useState<KYDAdminFeedItem | null>(null);

  const fetchFeed = useCallback(
    async (silent?: boolean, updateDirtyTextParams: any = 'all') => {
      if (!silent) {
        updateLoading("init", true);
      }
      let qr_codes;
      try {
        const _ = await actions.ADMIN.fetchBio();
        setFeed(_);

        setNewsOrder(_.news_items.map((item) => item.id));
        setMusicOrder(_.music_items.map((item) => item.id));

        updateDirtyText(updateDirtyTextParams);
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
      return qr_codes;
    },
    [updateLoading, updateDirtyText]
  );

  useEffect(() => {
    fetchFeed();
  }, [updateLoading, fetchFeed]);

  useEffect(() => {
    console.log("Music Order Changed: ", musicOrder);

    //@
    musicOrderRef.current = musicOrder;
  }, [musicOrder]);

  useEffect(() => {
    console.log("News Order Changed: ", newsOrder);

    //@ts-ignore
    newsOrderRef.current = newsOrder;
  }, [newsOrder]);

  const onCreateLink = useCallback(
    async (dt: any, type: KYDFeedItem["type"]) => {
      updateLoading("link", true);
      try {
        const city = dt["item.city"];
        const start_at = dt["item.start_at"];
        const venue_name = dt["item.venue_name"];
        const title = dt["item.title"];
        const timezone = dt["item.timezone"] || "America/New_York";
        const link = dt["item.link"];
        const image_override = dt["item.image_override"];
        const image = dt["item.image"];

        await actions.ADMIN.updateOrganization("create_link", {
          link,
          type,
          city,
          start_at,
          image,
          image_override,
          venue_name,
          title,
          timezone,
        });
        await fetchFeed(true);
        setEditingFeedItem(null);
        closeModal();
        toast({
          title: "Link Created",
          description: `Link has been created`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("link", false);
    },
    [fetchFeed, updateLoading, dialog, toast, closeModal]
  );

  const onUpdateLink = useCallback(
    async (
      linkId: string,
      update?: {
        values?: any;
        enabled?: boolean;
        should_delete?: boolean;
        order?: string[];
      }
    ) => {
      updateLoading(linkId, true);
      updateLoading("link", true);
      try {
        const updateRequest: any = {
          feed_item_id: linkId,
        };

        if (update && update.values) {
          updateRequest.image = update.values["item.image"];
          updateRequest.image_override = update.values["item.image_override"];
          updateRequest.title = update.values["item.title"];
          updateRequest.link = update.values["item.link"];
          updateRequest.city = update.values["item.city"];
          updateRequest.start_at = update.values["item.start_at"];
          updateRequest.venue_name = update.values["item.venue_name"];
          updateRequest.title = update.values["item.title"];
          updateRequest.timezone =
            update.values["item.timezone"] || "America/New_York";
        } else if (update && "enabled" in update) {
          updateRequest.status = update.enabled ? "OPEN" : "OPENDISABLED";
        } else if (update && update.should_delete) {
          updateRequest.status = "DELETED";
        } else if (linkId === "news_order") {
          updateRequest.order = newsOrderRef.current;
        } else if (linkId === "music_order") {
          updateRequest.order = musicOrderRef.current;
        }
        await actions.ADMIN.updateOrganization("update_link", updateRequest);
        await fetchFeed(true);
        setEditingFeedItem(null);
        closeModal();
        toast({
          title: "Link Updated",
          description: `Link has been updated`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading(linkId, false);
      updateLoading("link", false);
    },
    [fetchFeed, updateLoading, newsOrder, musicOrder, toast, dialog] // TODO: check is musicOrder and newsOrder are needed
  );

  const onUpdateFeedImage = useCallback(
    async (key: string) => {
      updateLoading("feed_image", true);
      try {
        await actions.ADMIN.updateOrganization("feed_image", { key });
        await fetchFeed(true);
        toast({
          title: "Feed Image Updated",
          description: `Feed image has been updated`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("feed_image", false);
    },
    [fetchFeed, updateLoading, dialog, toast]
  );

  const onUpdateSocial = useCallback(
    async (platform: KYDSocialPlatforms, link?: string, enabled?: boolean) => {
      updateLoading(`social.${platform}`, true);
      try {
        await actions.ADMIN.updateOrganization("social", {
          link,
          enabled,
          platform,
        });

        await fetchFeed(true, { [`${platform}.link`]: null });

        toast({
          title: `${capitalized(platform)} Updated`,
          description: `${capitalized(platform)} has been updated`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading(`social.${platform}`, false);
    },
    [fetchFeed, updateLoading, dialog, toast, dirtyText]
  );

  return (
    <Container p={0}>
      <EditLinkModal
        type={modalType}
        fetchMetadata={async (link) => actions.ADMIN.fetchLinkMetadata(link)}
        isOpen={!!modalType}
        onClose={() => {
          setEditingFeedItem(null);
          closeModal();
        }}
        feedItem={editingFeedItem}
        setEditingFeedItem={setEditingFeedItem}
        dirtyText={dirtyText}
        updateDirtyText={updateDirtyText}
        loading={loading}
        onCreateLink={onCreateLink}
        onUpdateLink={onUpdateLink}
        updateLoading={updateLoading}
      />
      {loading["init"] && (
        <VStack>
          <Spinner />
          <Text>Loading bio...</Text>
        </VStack>
      )}
      {!loading["init"] && !feed && (
        <Text>You don't have a KYD Feed yet. Please contact sales.</Text>
      )}

      {feed && (
        <Stack spacing={"50px"}>
          <Stack direction={{ base: "column", md: "column" }}>
            <VStack>
              <Image
                rounded={"md"}
                maxW={"300px"}
                src={feed.organization.feed_image}
              />
              <Button
                isDisabled={loading.feed_image || loading.blocking}
                isLoading={loading.feed_image}
                onClick={() => {
                  if (uploadRef && uploadRef.current) {
                    uploadRef.current.click();
                  }
                }}
                aria-label="Upload event image"
                variant={"outline"}
                leftIcon={<FaCloudUploadAlt />}
              >
                Upload KYD Feed Image
              </Button>
              {progress.poster > 0 && (
                <Progress
                  h="15px"
                  rounded={"lg"}
                  colorScheme={"green"}
                  value={progress.poster}
                />
              )}
              {(!progress.poster || progress.poster === 0) && (
                <Box h="15px" w="100%" />
              )}
              <ReactS3Uploader
                style={{ display: "none" }}
                //@ts-ignore
                inputRef={uploadRef}
                getSignedUrl={(file, callback) =>
                  actions.ADMIN.createUploadUrl(file, callback)
                }
                accept="image/png,image/jpg,image/jpeg"
                onProgress={(p) => {
                  updateProgress("poster", p);
                }}
                onError={(e) => {
                  updateLoading("event", false);
                  dialog({ text: e });
                }}
                preprocess={(f, next) => {
                  updateLoading("event", true);
                  next(f);
                }}
                onFinish={(result) => {
                  onUpdateFeedImage(result.fileKey);
                }}
                uploadRequestHeaders={(file) => ({
                  "Content-Type": file.type,
                  "x-amz-acl": "private"
                })}
                contentDisposition="auto"
              />
            </VStack>
            <HStack justify={"space-between"}>
              <Stack spacing={0}>
                <Text fontSize={"2xl"} fontWeight={"bold"}>
                  {feed.organization.name}
                </Text>
                <Text>{feed.organization.display_verified_fans}</Text>
              </Stack>
              {feed.organization.bio_link ? (
                <Button
                  variant={"outline"}
                  onClick={() =>
                    window.open(feed.organization.bio_link, "_blank")
                  }
                  rightIcon={<FiExternalLink />}
                >
                  {feed.organization.bio_link.split("https://").pop()}
                </Button>
              ) : null}
            </HStack>
            <Divider />
          </Stack>
          <Stack>
            <PageViews eventMetrics={feed.page_views} />
          </Stack>
          <Stack>
            <Text fontWeight="bold" fontSize="xl">
              Social Links
            </Text>
            <Text>Leave empty if not applicable (5 icons max)</Text>
            {SOCIAL_NETWORKS.map((social) => (
              <SocialInput
                key={social}
                type={social as KYDSocialPlatforms}
                dirtyText={dirtyText}
                feed={feed}
                loading={loading}
                onUpdateSocial={onUpdateSocial}
                updateDirtyText={updateDirtyText}
              />
            ))}
          </Stack>
          <Stack>
            <HStack w="100%" justify={"space-between"}>
              <Text fontWeight="bold" fontSize="xl">
                External Events
              </Text>
              <IconButton
                variant={"outline"}
                aria-label="add news item"
                icon={<FaPlus />}
                onClick={() => setModalType("external_event")}
              />
            </HStack>
            <Text>
              Add any upcoming events that aren't ticketed by KYD Labs. There
              are max 10 external events that can be created at at a time.
            </Text>
            <Divider />
            {feed.external_events.length > 0 ? (
              <List
                draggable={false}
                values={newsOrder}
                onReorder={setNewsOrder}
                axis="y"
                listStyleType={"none"}
              >
                <Stack>
                  {feed.external_events.map((item) => (
                    <FeedItem
                      key={item.id}
                      item={item}
                      loading={loading}
                      onUpdate={onUpdateLink}
                      onEdit={(item) => {
                        setModalType("external_event");
                        setEditingFeedItem(item);
                      }}
                    />
                  ))}
                </Stack>
              </List>
            ) : null}
            {feed.external_events.length === 0 ? (
              <Text fontSize={"sm"} color="gray.500">
                No external events yet
              </Text>
            ) : null}
          </Stack>
          <Stack>
            <HStack w="100%" justify={"space-between"}>
              <Text fontWeight="bold" fontSize="xl">
                In The News
              </Text>
              <IconButton
                variant={"outline"}
                aria-label="add news item"
                icon={<FaPlus />}
                onClick={() => setModalType("news")}
              />
            </HStack>
            <Text>
              Add any relevant press or news articles about you. Rank from 1 to
              10. There are max 10 links that can be created at at a time.
            </Text>
            <Divider />
            {feed.news_items.length > 0 ? (
              <List
                axis="y"
                values={newsOrder}
                onReorder={setNewsOrder}
                listStyleType={"none"}
              >
                <Stack>
                  {newsOrder
                    .map(
                      (id) => feed.news_items.find((item) => item.id === id)!
                    )
                    .map((item) => (
                      <FeedItem
                        key={item.id}
                        item={item}
                        loading={loading}
                        onUpdate={onUpdateLink}
                        onEdit={(item) => {
                          setModalType("news");
                          setEditingFeedItem(item);
                        }}
                      />
                    ))}
                </Stack>
              </List>
            ) : null}
            {feed.news_items.length === 0 ? (
              <Text fontSize={"sm"} color="gray.500">
                No news links added yet
              </Text>
            ) : null}
          </Stack>
          <Stack>
            <HStack w="100%" justify={"space-between"}>
              <Text fontWeight="bold" fontSize="xl">
                Music
              </Text>
              <IconButton
                variant={"outline"}
                aria-label="add music item"
                icon={<FaPlus />}
                onClick={() => setModalType("music")}
              />
            </HStack>
            <Text>
              Add any relevant press or news articles about you. Rank from 1 to
              10. There are max 10 links that can be created at at a time.
            </Text>
            <Divider />

            {feed.music_items.length > 0 ? (
              <List
                axis="y"
                values={musicOrder}
                onReorder={setMusicOrder}
                listStyleType={"none"}
              >
                <Stack>
                  {musicOrder
                    .map(
                      (id) => feed.music_items.find((item) => item.id === id)!
                    )
                    .map((item) => (
                      <FeedItem
                        key={item.id}
                        item={item}
                        loading={loading}
                        onUpdate={onUpdateLink}
                        onEdit={(item) => {
                          setModalType("music");
                          setEditingFeedItem(item);
                        }}
                      />
                    ))}
                </Stack>
              </List>
            ) : null}
            {feed.music_items.length === 0 ? (
              <Text fontSize={"sm"} color="gray.500">
                No music links added yet
              </Text>
            ) : null}
          </Stack>
        </Stack>
      )}
    </Container>
  );
};

export default FeedManager;
